import React, { useState } from 'react';
import { X } from 'lucide-react';
import { updatePartner } from '../../../constant/Constant';
import { HitApi } from '../../../Api/ApiHit';
import toast from 'react-hot-toast';

const EditPartnerModal = ({ partner, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    _id: partner._id,
    name: partner.name || '',
    phoneNumber: partner.phoneNumber || '',
    email: partner.email || '',
    gender: partner.gender || '',
    experience: partner.experience || '',
    specialisation: partner.specialisation?.join(', ') || '',
    address: partner.address || '',
    incomePercentage: partner.incomePercentage || 0,
    isActive: partner.isActive !== false,
  });

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!editData._id) return;
    setSaving(true);

    const payload = {
      _id: editData._id,
      name: editData.name,
      phoneNumber: editData.phoneNumber,
      email: editData.email,
      gender: editData.gender,
      experience: editData.experience,
      specialisation: editData.specialisation ? editData.specialisation.split(',').map(s => s.trim()).filter(Boolean) : [],
      address: editData.address,
      incomePercentage: parseFloat(editData.incomePercentage) || 0,
      isActive: editData.isActive,
    };

    HitApi(payload, updatePartner).then((result) => {
      if (result.success) {
        toast.success('Partner updated successfully');
        onSaved();
      } else {
        toast.error(result.message || 'Failed to update partner');
      }
    }).catch((error) => {
      console.error('Error updating partner:', error);
      toast.error('Failed to update partner');
    }).finally(() => {
      setSaving(false);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Edit Partner</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={editData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="text"
                value={editData.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={editData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select
                value={editData.gender}
                onChange={(e) => handleChange('gender', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 bg-white"
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
              <input
                type="text"
                value={editData.experience}
                onChange={(e) => handleChange('experience', e.target.value)}
                placeholder="e.g. 3 years"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Specialisation</label>
            <input
              type="text"
              value={editData.specialisation}
              onChange={(e) => handleChange('specialisation', e.target.value)}
              placeholder="Comma separated, e.g. Hair, Skin, Nails"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
            <p className="text-xs text-gray-400 mt-1">Separate multiple values with commas</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <textarea
              value={editData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Income Percentage (%)</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                value={editData.incomePercentage}
                onChange={(e) => handleChange('incomePercentage', e.target.value)}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
              />
              <div className="flex items-center gap-1 min-w-[60px]">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={editData.incomePercentage}
                  onChange={(e) => handleChange('incomePercentage', Math.min(100, Math.max(0, e.target.value)))}
                  className="w-14 px-2 py-1 text-sm text-center border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">Percentage of booking income the partner receives</p>
          </div>

          <div className="flex items-center justify-between pt-2">
            <label className="text-sm font-medium text-gray-700">Active Status</label>
            <button
              onClick={() => handleChange('isActive', !editData.isActive)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                editData.isActive ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                editData.isActive ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPartnerModal;
